import { useContext, useState } from "react";
import { AuthContext } from "../../../context/AuthContext";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
    Form,
    Input,
    Radio,
    Button,
    Upload,
    Typography,
    Card,
    DatePicker,
    Tag,
    Divider,
} from "antd";
import {
    UploadOutlined,
    CalendarOutlined,
    GlobalOutlined,
    ClockCircleOutlined,
} from "@ant-design/icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

export default function HrArea() {
    const { authData, backUrl } = useContext(AuthContext);
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [isFile, setIsFile] = useState(false);
    const [announcement_file, setAnnouncement_File] = useState(null);
    const [displayType, setDisplayType] = useState("STANDARD");
    const [dateRange, setDateRange] = useState([null, null]);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        if (!title.trim() || !content.trim()) {
            toast.error("Please enter both title and content.");
            return;
        }

        if (displayType === "FLEXIBLE") {
            if (!dateRange[0] || !dateRange[1]) {
                toast.error("Please select a start and end date for flexible announcements.");
                return;
            }
        }

        const formData = new FormData();
        formData.append("title", title);
        formData.append("content", content);
        formData.append("display_type", displayType);

        if (displayType === "FLEXIBLE") {
            formData.append("start_date", dateRange[0].toISOString());
            formData.append("end_date", dateRange[1].toISOString());
        }

        formData.append("is_file", isFile);
        if (isFile && announcement_file) {
            formData.append("announcement_file", announcement_file);
        }

        setLoading(true);
        try {
            const response = await axios.post(
                `${backUrl}/api/hr/announcements/`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${authData.access}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.status === 201 || response.status === 200) {
                toast.success("Announcement posted successfully!");
                setTitle("");
                setContent("");
                setIsFile(false);
                setAnnouncement_File(null);
                setDisplayType("STANDARD");
                setDateRange([null, null]);
            } else {
                toast.error("Failed to post announcement.");
            }
        } catch (error) {
            console.error("Error posting announcement:", error);
            toast.error("An error occurred while posting the announcement.");
        } finally {
            setLoading(false);
        }
    };

    const displayTypeInfo = {
        STANDARD: {
            color: "#000000",
            bg: "#ffffff",
            label: "Standard",
            description: "Always visible to everyone — no expiry.",
        },
        FLEXIBLE: {
            color: "#000000",
            bg: "#ffffff",
            label: "Flexible",
            description: "Visible only within your chosen date range.",
        },
    };

    const current = displayTypeInfo[displayType];

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div style={{ maxWidth: 680, margin: "0 auto", padding: "24px 16px" }}>
                    <Card
                        style={{
                            borderRadius: 16,
                            boxShadow: "0 6px 24px rgba(0,0,0,0.09)",
                            overflow: "hidden",
                            border: "none",
                        }}
                        bodyStyle={{ padding: "2rem 2.2rem" }}
                    >
                        {/* Header */}
                        <div style={{ textAlign: "center", marginBottom: 28 }}>
                            <Title
                                level={4}
                                style={{ color: "#0e497b", marginBottom: 4 }}
                            >
                                Post Announcement
                            </Title>
                        </div>

                        <Form layout="vertical" onFinish={handleSubmit}>

                            {/* Display Type Selector */}
                            <Form.Item >
                                <Radio.Group
                                    value={displayType}
                                    onChange={(e) => {
                                        setDisplayType(e.target.value);
                                        setDateRange([null, null]);
                                    }}
                                    style={{ width: "100%" }}
                                >
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                        {["STANDARD", "FLEXIBLE"].map((type) => {
                                            const info = displayTypeInfo[type];
                                            const isSelected = displayType === type;
                                            return (
                                                <Radio.Button
                                                    key={type}
                                                    value={type}
                                                    style={{
                                                        height: "auto",
                                                        padding: "12px 16px",
                                                        borderRadius: 10,
                                                        border: isSelected
                                                            ? `2px solid ${info.color}`
                                                            : "2px solid #ffffff",
                                                        background: isSelected ? info.bg : "#ffffff",
                                                        textAlign: "left",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 4,
                                                        transition: "all 0.2s",
                                                        lineHeight: "normal",
                                                        whiteSpace: "normal",
                                                    }}
                                                >
                                                    <div style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 6,
                                                        fontWeight: 600,
                                                        color: isSelected ? info.color : "#555",
                                                        fontSize: 14,
                                                    }}>
                                                        {info.label}
                                                    </div>
                                                    <div style={{
                                                        fontSize: 12,
                                                        color: "#888",
                                                        fontWeight: 400,
                                                    }}>
                                                        {info.description}
                                                    </div>
                                                </Radio.Button>
                                            );
                                        })}
                                    </div>
                                </Radio.Group>

                                {/* Active badge */}
                                <div style={{ marginTop: 10 }}>
                                    <Tag
                                        style={{ borderRadius: 5, padding: "2px 10px", fontSize: 12 }}
                                    >
                                        {current.label} mode selected
                                    </Tag>
                                </div>
                            </Form.Item>

                            {/* Flexible Date Range */}
                            <AnimatePresence>
                                {displayType === "FLEXIBLE" && (
                                    <motion.div
                                        key="dateRange"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.3 }}
                                        style={{ overflow: "hidden" }}
                                    >
                                        <Form.Item
                                            label={
                                                <span>
                                                    <CalendarOutlined style={{ marginRight: 6, color: "#ffffff" }} />
                                                    <Text strong>Visibility Window</Text>
                                                </span>
                                            }
                                            required
                                            style={{
                                                background: "#ffffff",
                                                border: "1px dashed #ffffff",
                                                borderRadius: 10,
                                                padding: "14px 16px",
                                            }}
                                        >
                                            <RangePicker
                                                showTime
                                                format="YYYY-MM-DD HH:mm"
                                                style={{ width: "100%", borderRadius: 8 }}
                                                value={
                                                    dateRange[0] && dateRange[1]
                                                        ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
                                                        : null
                                                }
                                                onChange={(dates) => {
                                                    if (dates) {
                                                        setDateRange([dates[0].toDate(), dates[1].toDate()]);
                                                    } else {
                                                        setDateRange([null, null]);
                                                    }
                                                }}
                                                disabledDate={(current) =>
                                                    current && current < dayjs().startOf("day")
                                                }
                                                placeholder={["Start Date & Time", "End Date & Time"]}
                                            />
                                        </Form.Item>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <Divider style={{ margin: "4px 0 16px" }} />

                            {/* Title */}
                            <Form.Item label={<Text strong>Title</Text>} required>
                                <Input
                                    placeholder="Enter announcement title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    style={{ borderRadius: 8 }}
                                    size="large"
                                />
                            </Form.Item>

                            {/* Content */}
                            <Form.Item label={<Text strong>Content</Text>} required>
                                <TextArea
                                    rows={4}
                                    placeholder="Main body of the announcement"
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    style={{ borderRadius: 8, resize: "vertical" }}
                                />
                            </Form.Item>

                            {/* Upload File Option */}
                            <Form.Item label={<Text strong>Attach a File?</Text>}>
                                <Radio.Group
                                    value={isFile}
                                    onChange={(e) => setIsFile(e.target.value)}
                                    optionType="button"
                                    buttonStyle="solid"
                                    style={{ marginBottom: 12 }}
                                >
                                    <Radio value={true}>Yes</Radio>
                                    <Radio value={false}>No</Radio>
                                </Radio.Group>

                                <AnimatePresence>
                                    {isFile && (
                                        <motion.div
                                            key="fileUpload"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.25 }}
                                            style={{ overflow: "hidden" }}
                                        >
                                            <Upload
                                                beforeUpload={(file) => {
                                                    setAnnouncement_File(file);
                                                    return false;
                                                }}
                                                maxCount={1}
                                                showUploadList={false}
                                            >
                                                <Button
                                                    icon={<UploadOutlined />}
                                                    style={{ borderRadius: 8 }}
                                                >
                                                    Select File
                                                </Button>
                                            </Upload>
                                            {announcement_file && (
                                                <Tag
                                                    color="green"
                                                    style={{ marginTop: 8, borderRadius: 6, padding: "2px 10px" }}
                                                >
                                                    📎 {announcement_file.name}
                                                </Tag>
                                            )}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </Form.Item>

                            {/* Submit */}
                            <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    loading={loading}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        borderColor: "#0e497b",
                                        borderRadius: 10,
                                        fontSize: 15,
                                        fontWeight: 600,
                                        height: 48,
                                        letterSpacing: 0.3,
                                    }}
                                >
                                    {loading ? "Posting..." : "Make Announcement"}
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </div>
            </motion.div>
        </>
    );
}